import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { translations } from "@/i18n/translations";
import { API } from "@/components/dashboard/constants";

const DashboardContext = createContext();

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error("useDashboard must be used within a DashboardProvider");
  }
  return context;
};

export const DashboardProvider = ({ children }) => {
  // Language
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "en");
  const t = translations[lang];
  
  // Default period (loaded from settings API, fallback: 7 days)
  const [defaultPeriodDays, setDefaultPeriodDays] = useState(() => {
    const saved = localStorage.getItem("defaultPeriodDays");
    return saved ? parseInt(saved, 10) : 7;
  });
  
  // Date range (uses defaultPeriodDays)
  const [startDate, setStartDate] = useState(() => {
    const days = parseInt(localStorage.getItem("defaultPeriodDays") || "7", 10);
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date;
  });
  const [endDate, setEndDate] = useState(() => new Date());
  
  // Accounts and filters
  const [accounts, setAccounts] = useState([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  
  // Summary data (for header)
  const [summary, setSummary] = useState(null);
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Save language preference
  useEffect(() => {
    localStorage.setItem("lang", lang);
  }, [lang]);
  
  // Fetch accounts
  const fetchAccounts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/accounts`);
      setAccounts(res.data);
    } catch (error) {
      console.error("Failed to fetch accounts:", error);
    }
  }, []);
  
  // Load default period from API on mount
  useEffect(() => {
    axios.get(`${API}/settings/default-period`).then(res => {
      const days = res.data?.period_days || 7;
      setDefaultPeriodDays(days);
      localStorage.setItem("defaultPeriodDays", String(days));
    }).catch(() => {});
  }, []);
  
  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);
  
  // Fetch summary data
  const fetchSummary = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate.toISOString().split("T")[0]);
      if (endDate) params.append("end_date", endDate.toISOString().split("T")[0]);
      if (selectedPlatforms.length > 0) params.append("platforms", selectedPlatforms.join(","));
      if (selectedAccounts.length > 0) params.append("account_ids", selectedAccounts.join(","));
      
      const res = await axios.get(`${API}/dashboard/summary?${params.toString()}`);
      setSummary(res.data);
    } catch (error) {
      console.error("Failed to fetch summary:", error);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedPlatforms, selectedAccounts]);
  
  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);
  
  // Refresh data
  const refreshData = useCallback(async () => {
    setRefreshing(true);
    await fetchSummary();
    await fetchAccounts();
    setRefreshing(false);
  }, [fetchSummary, fetchAccounts]);
  
  const value = {
    // Language
    lang,
    setLang,
    t,
    
    // Date range
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    defaultPeriodDays,
    setDefaultPeriodDays,
    
    // Accounts and filters
    accounts,
    selectedPlatforms,
    setSelectedPlatforms,
    selectedAccounts,
    setSelectedAccounts,
    
    // Summary
    summary,
    setSummary,
    
    // Loading states
    loading,
    setLoading,
    refreshing,
    
    // Actions
    refreshData,
    fetchAccounts,
    fetchSummary,
  };
  
  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};

export default DashboardContext;
